import React, { useCallback, memo } from 'react';
import { DragIndicator } from '@mui/icons-material';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components';

import ControlledTextField from 'components/ControlledTextField';
import DeleteIconButton from 'components/DeleteIconButton';
import UpDown from 'components/UpDown';
import { Action } from 'state';
import Section from 'types/Section';
import SplitButton from 'components/SplitButton';
import MeasurementType from 'types/MeasurementType';
import TemplateType from 'types/TemplateType';

const options = [
  { value: null, label: '-' },
  { value: MeasurementType.LINEAR, label: 'Add Linear measurement' },
  { value: MeasurementType.VTI, label: 'Add VTI measurement' },
  { value: MeasurementType.ELLIPTICAL, label: 'Add Elliptical measurement' },
];

const StyledSectionName = styled.div`
  display: flex;
  align-items: center;

  & > *:not(:last-child) {
    margin-right: 0.5rem;
  }
`;

interface SectionHeaderProps {
  section: Section;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  dispatch: (action: Action) => void;
  templateType: TemplateType;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ section, dragHandleProps, dispatch, templateType }) => {
  const handleOnChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value: label } = e.target;
      dispatch({ type: 'set-section-label', section, label });
    },
    [dispatch, section],
  );

  const handleOnClickDelete = useCallback(() => {
    dispatch({ type: 'remove-section', section });
  }, [dispatch, section]);

  const handleMoveSectionUp = useCallback(() => {
    dispatch({ type: 'move-section-up', sectionUuid: section.uuid, offset: 1 });
  }, [dispatch, section]);

  const handleMoveSectionDown = useCallback(() => {
    dispatch({ type: 'move-section-down', sectionUuid: section.uuid, offset: 1 });
  }, [dispatch, section]);

  const handleClick = useCallback(
    (key: string | null) => {
      dispatch({
        type: 'set-section-measurement-type',
        sectionUuid: section.uuid,
        measurementType: key as MeasurementType,
      });
    },
    [dispatch, section],
  );

  return (
    <StyledSectionName>
      <div {...dragHandleProps}>
        <DragIndicator fontSize="small" />
      </div>
      <UpDown onUp={handleMoveSectionUp} onDown={handleMoveSectionDown} />
      <ControlledTextField
        {...(templateType === TemplateType.QA && !section.credits ? { required: true } : {})}
        label="Section Label"
        variant="outlined"
        size="small"
        fullWidth
        value={section.label}
        onChange={handleOnChangeName}
      />
      <SplitButton options={options} optionSelected={section.measurement_type} onOptionChange={handleClick} />
      <DeleteIconButton onClick={handleOnClickDelete} />
    </StyledSectionName>
  );
};

export default memo(SectionHeader);
